import React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  BookOpen,
  CheckCircle2,
  CircleHelp,
  Mail,
  Phone,
  Smartphone,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface SupportActionProps {
  title: string;
  description: string;
  icon: React.ElementType;
  onClick: () => void;
  testId: string;
}

const SupportAction: React.FC<SupportActionProps> = ({ title, description, icon: Icon, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group flex min-h-[92px] w-full items-start gap-3 rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 text-left transition-colors hover:bg-[var(--gray-50)] sm:p-5"
  >
    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-primary)]/10">
      <Icon className="h-5 w-5 text-[var(--color-primary)]" />
    </div>
    <div className="min-w-0 flex-1">
      <h2 className="font-semibold text-[var(--text-primary)]">{title}</h2>
      <p className="mt-1 text-sm leading-relaxed text-[var(--text-secondary)]">{description}</p>
    </div>
    <ArrowRight className="mt-1 h-5 w-5 shrink-0 text-[var(--text-tertiary)] transition-transform group-hover:translate-x-0.5" />
  </button>
);

const SoverdiSupportR7: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-5 pb-24 sm:space-y-6 lg:pb-6" data-testid="client-support-r7">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-[var(--color-primary)]">
            <CircleHelp className="h-4 w-4" />
            <span className="text-xs font-semibold uppercase tracking-wide">Aide</span>
          </div>
          <h1 className="mt-1 text-xl font-semibold text-[var(--text-primary)] sm:text-2xl">Assistance</h1>
          <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
            Identifiez rapidement l’écran utile avant de contacter Agri Safe. Cette page ne crée pas de ticket et ne modifie aucune configuration terrain.
          </p>
        </div>
        <span className="inline-flex w-fit items-center rounded-full border border-[var(--border-light)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)]">
          Diagnostic guidé
        </span>
      </section>

      <section>
        <div className="mb-3">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Que souhaitez-vous vérifier ?</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Ces raccourcis ouvrent uniquement des interfaces client déjà raccordées aux données Agri Safe.
          </p>
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <SupportAction
            title="J’ai une alerte"
            description="Ouvrir la liste réelle des alertes pour vérifier le type, la gravité et l’état de l’événement."
            icon={AlertTriangle}
            onClick={() => navigate('/r6-soverdi-alerts')}
            testId="support-open-alerts"
          />
          <SupportAction
            title="Un équipement ne remonte plus"
            description="Consulter les équipements, leur fraîcheur de communication et leur affectation métier."
            icon={Smartphone}
            onClick={() => navigate('/r6-soverdi-equipment')}
            testId="support-open-equipment"
          />
          <SupportAction
            title="Comprendre l’interface"
            description="Ouvrir le guide client intégré pour retrouver le rôle des principaux écrans et des états affichés."
            icon={BookOpen}
            onClick={() => navigate('/r6-soverdi-documentation')}
            testId="support-open-documentation"
          />
          <SupportAction
            title="Contacter Agri Safe"
            description="Retrouver les coordonnées publiques Agri Safe et les informations utiles à transmettre à l’équipe."
            icon={CircleHelp}
            onClick={() => navigate('/r6-soverdi-contact')}
            testId="support-open-contact"
          />
        </div>
      </section>

      <section className="rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 sm:p-5">
        <h2 className="font-semibold text-[var(--text-primary)]">Avant de nous contacter</h2>
        <div className="mt-4 grid gap-3 md:grid-cols-3">
          {[
            ['Site concerné', 'Indiquez le site et, si possible, le nom métier du secteur ou de la parcelle.'],
            ['Ce que vous observez', 'Décrivez l’écran, le message visible et l’heure approximative du constat.'],
            ['Aucune donnée sensible', 'Ne transmettez jamais de mot de passe, clé AWS, secret TTN ou autre secret technique.'],
          ].map(([title, detail]) => (
            <div key={title} className="flex items-start gap-2 rounded-xl bg-[var(--gray-50)] p-3">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-primary)]" />
              <div>
                <p className="text-sm font-semibold text-[var(--text-primary)]">{title}</p>
                <p className="mt-1 text-xs leading-relaxed text-[var(--text-secondary)]">{detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2">
        <a
          href="mailto:contact@agrisafe.fr?subject=Assistance%20Agri%20Safe"
          data-testid="support-email"
          className="flex min-h-[72px] items-center gap-3 rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 transition-colors hover:bg-[var(--gray-50)]"
        >
          <Mail className="h-5 w-5 text-[var(--color-primary)]" />
          <div>
            <p className="font-semibold text-[var(--text-primary)]">contact@agrisafe.fr</p>
            <p className="text-xs text-[var(--text-secondary)]">Ouvrir votre messagerie</p>
          </div>
        </a>
        <a
          href="tel:+33695464339"
          data-testid="support-phone"
          className="flex min-h-[72px] items-center gap-3 rounded-2xl border border-[var(--border-light)] bg-[var(--surface)] p-4 transition-colors hover:bg-[var(--gray-50)]"
        >
          <Phone className="h-5 w-5 text-[var(--color-primary)]" />
          <div>
            <p className="font-semibold text-[var(--text-primary)]">+33 6 95 46 43 39</p>
            <p className="text-xs text-[var(--text-secondary)]">Appel direct depuis un appareil compatible</p>
          </div>
        </a>
      </section>

      <section className="rounded-xl border border-[var(--border-light)] bg-[var(--gray-50)] p-4 text-sm text-[var(--text-secondary)] sm:p-5">
        Aucun dossier d’assistance n’est créé automatiquement depuis cette page. Les raccourcis de diagnostic restent en lecture seule et les boutons de contact ouvrent simplement les applications de votre appareil.
      </section>
    </div>
  );
};

export default SoverdiSupportR7;
